import { useEffect, useState } from 'react';
import { QuickstartCard } from './QuickstartCard';
import { QuickstartTheme, getResponsiveTextSize } from '../types/theme';
import { useIsLandscape, useIsPortraitOrientation } from '../utils/responsiveHooks';

interface QuickstartLoadingProps {
  theme: QuickstartTheme;
  message?: string;
  badgeText?: string;
}

const loadingMessages = [
  'Searching for available machine...',
  'Authenticating credentials...',
  'Establishing secure link...',
  'Synchronizing broadcast data...',
  'Allocating system resources...',
  'Optimizing connection parameters...',
  'Finalizing environment setup...',
  'Verifying runtime integrity...',
  'Preparing broadcast handshake...',
  'Stabilizing connection...',
  'Performing system checks...',
  'Balancing server load...',
  'Negotiating protocols...',
  'Confirming network stability...',
  'Validating broadcast tokens...',
];

export const QuickstartLoading = ({ theme, message, badgeText }: QuickstartLoadingProps) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const isLandscape = useIsLandscape();

  // Detect actual physical orientation for rotation
  const isPortraitOrientation = useIsPortraitOrientation();

  // Apply rotation on mobile devices in portrait orientation
  const shouldRotate = isLandscape && isPortraitOrientation;

  useEffect(() => {
    // If a custom message is provided, don't cycle
    if (message) return;

    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;

        // If we've reached the end of the list
        if (nextIndex >= loadingMessages.length) {
          // Skip the first message on subsequent cycles
          return 1;
        }

        return nextIndex;
      });
    }, 8000); // Change message every 8 seconds

    return () => clearInterval(interval);
  }, [message]);

  const displayMessage = message || loadingMessages[currentMessageIndex];

  return (
    <div
      className='flex items-center justify-center overflow-auto'
      style={
        shouldRotate ? {
          // Portrait mode: swap dimensions and rotate
          position: 'fixed',
          left: '50%',
          top: '50%',
          width: '100dvh',
          height: '100dvw',
          transform: 'translate(-50%, -50%) rotate(var(--force-rotate-angle, 90deg))',
          transformOrigin: 'center center',
          backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '1rem',
        } : {
          // Normal mode
          minHeight: '100vh',
          backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '1rem',
        }
      }
    >
      <QuickstartCard theme={theme} badgeOverride={badgeText ?? 'SNEAK PEEK'}>
        <div className={`flex flex-col items-center ${isLandscape ? 'space-y-3' : 'space-y-6'}`}>
          {/* Loading Spinner */}
          <div
            className={`${isLandscape ? 'h-10 w-10' : 'h-16 w-16'} animate-spin rounded-full border-4 border-transparent`}
            style={{
              borderTopColor: theme.spinnerColor || theme.primaryColor,
              borderRightColor: theme.spinnerColor || theme.primaryColor,
            }}
          />

          {/* Message */}
          <p
            className={`max-w-[200px] text-center ${getResponsiveTextSize('base', isLandscape)} ${isLandscape ? 'h-8' : 'h-12'} flex items-center justify-center`}
            style={{ color: '#FEF3C7' }}
          >
            {displayMessage}
          </p>
        </div>
      </QuickstartCard>
    </div>
  );
};

